import { ActionTree } from 'vuex';
import { editorContentNamespace, EEditorContentAction } from './editor-content.constants';
import {
  EEditorUiAction,
  EEditorUiMutation,
} from '@/editor/editor-block/store/editor-ui.constants';
import { IEditorUiState, IUpdateRequest } from '@/editor/editor-block/store/editor-ui.types';
import { RootState } from '@/store/store';
import {
  ICreateImage,
  ISelection,
  IStoredImage,
  IMenuCommand,
  ITextMenuState,
  IImageMenuState,
  IHover,
  IEditorBlock,
} from '@/editor/types';
import { EditorBlockService } from '@/editor/api/services/editor-block.service';
import { translate } from '@/helpers/translation.helper';
import { EContext } from '@/editor/types/enums';
import { getterDocumentId } from '@/dealroom-view/store/dealroom-view.getters';

export const actions: ActionTree<IEditorUiState, RootState> = {
  [EEditorUiAction.SetContext]({ commit }, context: EContext) {
    commit(EEditorUiMutation.SetContext, context);
  },

  [EEditorUiAction.SetSelection]({ commit }, selection: ISelection) {
    commit(EEditorUiMutation.SetSelection, selection);
  },

  [EEditorUiAction.ResetSelection]({ commit }) {
    commit(EEditorUiMutation.ResetSelection);
  },

  [EEditorUiAction.SetHover]({ commit }, hover: IHover) {
    commit(EEditorUiMutation.SetHover, hover);
  },

  [EEditorUiAction.ResetHover]({ commit }) {
    commit(EEditorUiMutation.ResetHover);
  },

  [EEditorUiAction.UpdateTextMenuState]({ commit }, textMenuState: ITextMenuState) {
    commit(EEditorUiMutation.UpdateTextMenuState, textMenuState);
  },

  [EEditorUiAction.UpdateImageMenuState]({ commit }, imageMenuState: Partial<IImageMenuState>) {
    commit(EEditorUiMutation.UpdateImageMenuState, imageMenuState);
  },

  [EEditorUiAction.TriggerMenuCommand]({ commit }, menuCommand: IMenuCommand) {
    commit(EEditorUiMutation.TriggerMenuCommand, menuCommand);
  },

  async [EEditorUiAction.CreateImage](
    { commit, dispatch },
    { contentId, createImage }: { contentId: string; createImage: ICreateImage }
  ) {
    commit(EEditorUiMutation.SetImageUploading);

    try {
      const image: IStoredImage = await EditorBlockService.createImage(createImage);
      dispatch(EEditorUiAction.CreateImageSuccess, { contentId, image });
    } catch (error) {
      dispatch(EEditorUiAction.CreateImageFailed, { contentId });
    }
  },

  [EEditorUiAction.CreateImageSuccess](
    { commit },
    { contentId, image }: { contentId: string; image: IStoredImage }
  ) {
    commit(EEditorUiMutation.SetImageUploaded, { contentId, image });
  },

  [EEditorUiAction.CreateImageFailed]({ commit, dispatch }, { contentId }: { contentId: string }) {
    commit(EEditorUiMutation.SetImageUploadFailed, { contentId });
    dispatch('alert/showDangerAlert', translate('Failed-to-upload-image'), {
      root: true,
    });
  },

  [EEditorUiAction.ResetCreateImage]({ commit }) {
    commit(EEditorUiMutation.ResetImageUpload);
  },

  [EEditorUiAction.SetBlockToUpdate]({ commit }, block: IEditorBlock) {
    commit(EEditorUiMutation.SetBlockToUpdate, block);
  },

  [EEditorUiAction.EnqueueRequest]({ commit, dispatch, state }, requestPayload: IUpdateRequest) {
    commit(EEditorUiMutation.EnqueueRequest, requestPayload);
    if (!state.isUpdating) {
      dispatch(EEditorUiAction.ProcessQueue);
    }
  },

  async [EEditorUiAction.ProcessQueue]({ commit, dispatch, state }) {
    commit(EEditorUiMutation.SetIsUpdating, true);
    while (state.updateQueue.length > 0) {
      const { contentId, type, payload, blockBeforeUpdate } = state.updateQueue[0];
      try {
        await EditorBlockService.dispatchRequest(payload, type);
        dispatch(EEditorUiAction.ProcessQueueSuccess, contentId);
      } catch (error) {
        dispatch(EEditorUiAction.ProcessQueueFailed, blockBeforeUpdate);
      }
    }
    commit(EEditorUiMutation.SetIsUpdating, false);
  },

  [EEditorUiAction.ProcessQueueSuccess](
    { commit, dispatch, state, rootGetters },
    contentId: string
  ) {
    commit(EEditorUiMutation.DequeueRequest);

    if (state.context === EContext.DocumentView) {
      const documentId: string = rootGetters[`${'dealroomView'}/${getterDocumentId}`];

      dispatch(`${editorContentNamespace}/${EEditorContentAction.UpdateDocumentPage}`, contentId, {
        root: true,
      });
      dispatch(`${'dealroomView'}/${'loadDocumentRevisions'}`, documentId, {
        root: true,
      });
    }
  },

  async [EEditorUiAction.ProcessQueueFailed](
    { commit, dispatch, state },
    blockBeforeUpdate: IEditorBlock
  ) {
    const { context } = state;
    commit(EEditorUiMutation.EmptyQueue, {
      root: true,
    });
    if (context === EContext.Document) {
      dispatch(
        `${editorContentNamespace}/${EEditorContentAction.ContentUpdateFailed}`,
        blockBeforeUpdate,
        {
          root: true,
        }
      );
    } else if (context === EContext.DocumentView) {
      dispatch(
        `${'dealroomView'}/${'editorUpdateFailed'}`,
        {},
        {
          root: true,
        }
      );
    }
  },
};
